import Uri from 'jsuri';
import { isEmpty } from 'lodash';
import difference from 'lodash/difference';
import { batch } from 'react-redux';
import { SECURE_BASE_URL } from '../../config';
import { SignupStep } from '../../types/billing';
import { AppDispatch, DispatchThunk } from '../../types/common';
import { CATCH_ERROR } from '../../utils/defaults';
import { fetchWithRetry } from '../../utils/promise';
import {
  setSelectedAddOnIds,
  setSharedBillingInfo,
} from '../../web-shared/redux/actions/shared-billing';
import { getSelectedAddOnIds } from '../../web-shared/redux/selectors/shared-billing';
import { BillItem, PackageId, Packages } from '../../web-shared/types/billing';
import {
  PHILO_CORE_PLANS,
  checkResponse,
  extractErrorMessage,
} from '../../web-shared/utils/billing';
import { BillingState } from '../defaults/billing';
import { RootState } from '../reducers';
import { getIsLoadingPackageDetails, getSelectedAddOns } from '../selectors/billing';

export const SET_IS_LOADING_SUBSCRIPTION_START = 'APP_SET_IS_LOADING_SUBSCRIPTION_START';
export const SET_PACKAGE_DETAILS_ARE_LOADED = 'APP_SET_PACKAGE_DETAILS_ARE_LOADED';
export const SET_SELECTED_PACKAGE = 'SET_SELECTED_PACKAGE';
export const UPDATE_BILLING_INFO = 'APP_UPDATE_BILLING_INFO';

export const SET_IS_AUTHENTICATED = 'SET_IS_AUTHENTICATED';
export const SET_IS_SUBSCRIBED = 'SET_IS_SUBSCRIBED';
export const SET_IS_DIRECT_SIGNUP = 'SET_IS_DIRECT_SIGNUP';

export const SET_CREDIT_CARD_INFORMATION = 'SET_CREDIT_CARD_INFORMATION';
export const SET_SHOW_SUBSCRIPTION_CODE_FIELD = 'SET_SHOW_SUBSCRIPTION_CODE_FIELD';
export const SET_SUBSCRIPTION_CODE_INPUT = 'SET_SUBSCRIPTION_CODE_INPUT';

// MM: exported for storybook testing
export const LOAD_SIGNUP_DETAILS_URL = `${SECURE_BASE_URL}/billing/packages`;

export function setIsAuthenticated(isAuthenticated: BillingState['isAuthenticated']) {
  return {
    type: SET_IS_AUTHENTICATED,
    isAuthenticated,
  };
}

export function setShowSubscriptionCodeField(
  showSubscriptionCodeField: BillingState['showSubscriptionCodeField'],
) {
  return {
    type: SET_SHOW_SUBSCRIPTION_CODE_FIELD,
    payload: {
      showSubscriptionCodeField,
    },
  };
}

export function setSubscriptionCodeInput(
  subscriptionCodeInput: BillingState['subscriptionCodeInput'],
) {
  return {
    type: SET_SUBSCRIPTION_CODE_INPUT,
    payload: {
      subscriptionCodeInput,
    },
  };
}

export interface UpdateBillingInfoAction {
  type: typeof UPDATE_BILLING_INFO;
  payload: Partial<BillingState>;
}

export function updateBillingInfo(payload: Partial<BillingState>): UpdateBillingInfoAction {
  return {
    type: UPDATE_BILLING_INFO,
    payload,
  };
}

export function setNeedsValidPaymentInfo(
  needsValidPaymentInfo: BillingState['needsValidPaymentInfo'],
) {
  return updateBillingInfo({
    needsValidPaymentInfo,
  });
}

export function setCanProvidePayment(canProvidePayment: BillingState['canProvidePayment']) {
  return updateBillingInfo({
    canProvidePayment,
  });
}

export function setSelectedAddOns(addOn: BillItem): DispatchThunk {
  return (dispatch, getState) => {
    const state = getState();
    const selectedAddOnIds = getSelectedAddOnIds(state);
    const currentSelectedAddOns = getSelectedAddOns(state);
    const currentSelectedAddOnIds = { ...selectedAddOnIds };
    let selectedAddOns: BillItem[];

    if (currentSelectedAddOns.includes(addOn)) {
      selectedAddOns = difference(currentSelectedAddOns, [addOn]);
      delete currentSelectedAddOnIds[addOn.id];
    } else {
      selectedAddOns = [...currentSelectedAddOns, addOn];
      currentSelectedAddOnIds[addOn.id] = true;
    }
    batch(() => {
      dispatch(
        updateBillingInfo({
          selectedAddOns,
        }),
      );
      dispatch(setSelectedAddOnIds(currentSelectedAddOnIds));
    });
  };
}

export function setSignupStep(signupStep?: number) {
  return updateBillingInfo({
    signupStep,
  });
}

export function setBillingErrorMessage(errorMessage: BillingState['billingErrorMessage']) {
  return updateBillingInfo({
    billingErrorMessage: errorMessage,
  });
}

export function setIsLoadingSubscriptionStart(
  isLoadingSubscriptionStart: BillingState['isLoadingSubscriptionStart'],
) {
  return {
    type: SET_IS_LOADING_SUBSCRIPTION_START,
    payload: {
      isLoadingSubscriptionStart,
    },
  };
}

export function setIsSubscribed(isSubscribed: BillingState['isSubscribed']) {
  return {
    type: SET_IS_SUBSCRIBED,
    payload: {
      isSubscribed,
    },
  };
}

export function setHasAgreedToPaymentTerms(
  hasAgreedToPaymentTerms: BillingState['hasAgreedToPaymentTerms'],
) {
  return updateBillingInfo({ hasAgreedToPaymentTerms });
}

export function setHasAgreedToSignupTerms(
  hasAgreedToSignupTerms: BillingState['hasAgreedToSignupTerms'],
) {
  return updateBillingInfo({ hasAgreedToSignupTerms });
}

export function processSignupDetailsResponse(
  response: {
    result: Record<string, any>;
  },
  { preSelectedAddOnId = '', defaultPackageId = Packages.EMPTY_ID } = {},
): DispatchThunk {
  return dispatch => {
    const { plans = [], addons: addOns = [], addonPromotionalDetails } = response.result;

    let selectedPackageId = plans[0]?.id;

    const planExistsForDefaultPackageId =
      !!defaultPackageId && plans.some((plan: BillItem) => plan.id === defaultPackageId);
    if (planExistsForDefaultPackageId) {
      selectedPackageId = defaultPackageId;
    } else if (plans.length > 1) {
      selectedPackageId =
        plans.find((plan: BillItem) => PHILO_CORE_PLANS.has(plan.id))?.id || selectedPackageId;
    }

    const preSelectedAddOn: BillItem = addOns.find(
      (addOn: BillItem) => addOn.id === preSelectedAddOnId,
    );
    const selectedAddOnIds = {} as Record<PackageId, boolean>;
    let selectedAddOns: BillItem[] = [];

    if (!isEmpty(preSelectedAddOn)) {
      const { id } = preSelectedAddOn;
      selectedAddOnIds[id] = true;
      selectedAddOns = [preSelectedAddOn];
    }

    batch(() => {
      dispatch(
        setSharedBillingInfo({
          selectedAddOnIds,
          selectedPackageId,
        }),
      );

      dispatch(
        updateBillingInfo({
          addOnPromoDetails: addonPromotionalDetails,
          addOns,
          selectedAddOns,
          isLoadingPackageDetails: false,
          packageDetailsAreLoaded: true,
          selectablePlans: plans,
        }),
      );
    });
  };
}

// SC NOTE: error is not currently user facing & is not tracked via errorMessageDisplayed analytics event.
export function loadPackageDetails({
  selectedAddOnId,
  defaultPackageId,
}: {
  selectedAddOnId?: string;
  defaultPackageId?: PackageId;
} = {}) {
  return async (dispatch: AppDispatch, getState: () => RootState) => {
    const state = getState();

    if (getIsLoadingPackageDetails(state)) {
      return Promise.resolve();
    }

    dispatch(
      updateBillingInfo({
        packageDetailsAreLoaded: false,
        isLoadingPackageDetails: true,
      }),
    );

    const signupDetailsURL = new Uri(LOAD_SIGNUP_DETAILS_URL);

    return fetchWithRetry(signupDetailsURL.toString(), {
      method: 'GET',
      credentials: 'include',
    })
      .then(checkResponse)
      .then(json => {
        if (!json.success) {
          const defaultErrorMessage =
            'Oops! Failed to load information about subscription packages.';
          const { errorMessage } = extractErrorMessage(json, defaultErrorMessage);
          dispatch(setBillingErrorMessage(errorMessage));
          CATCH_ERROR(json.error);
          return;
        }

        dispatch(
          processSignupDetailsResponse(json, {
            preSelectedAddOnId: selectedAddOnId,
            defaultPackageId,
          }),
        );
      })
      .catch(error => {
        dispatch(
          updateBillingInfo({
            isLoadingPackageDetails: false,
          }),
        );
        dispatch(
          setBillingErrorMessage(
            `Oops! Caught an exception while loading subscription packages: ${error.message}`,
          ),
        );
        CATCH_ERROR(error);
      });
  };
}

export function enableKnownUserSignup() {
  return updateBillingInfo({
    isAuthenticated: true,
    canProvidePayment: true,
    signupStep: SignupStep.PAYMENT,
    hasAgreedToSignupTerms: true,
  });
}

export function setIsFASTSignUp(isFASTSignUp: BillingState['isFASTSignUp']) {
  return updateBillingInfo({
    isFASTSignUp,
  });
}
